<template>
    <div class="pmt-container">
        <div class="confirm-svg">
            <svg style="padding-left: 5px;" height="40px" id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 156 180.36">
                <g id="image">
                    <path d="M83.82,107.32c-.8,.6-1.89,.64-2.73,.09-9.02-5.68-17.02-12.82-23.68-21.13-8.16-9.91-13.64-21.75-15.94-34.38-1.89-11.72-.09-22.99,5.78-32.17,2.37-3.73,5.35-7.03,8.8-9.77C63.62,3.64,73.14,.13,82.99,0c9.55,.15,18.69,3.88,25.62,10.45,2.66,2.44,4.96,5.25,6.84,8.33,6.27,10.27,7.62,23.48,4.86,36.86-4.67,21.46-17.84,40.1-36.49,51.68Zm-26.82,1.36v40.6l44,18.97v-52.69l8-7.93v60.34l37-18.63V67.47l-15.02,5.78c1.51-3.98,2.75-8.06,3.72-12.2l14.53-5.61c2.58-.94,5.44,.36,6.42,2.92,.23,.58,.34,1.19,.34,1.81v92.18c0,1.93-1.16,3.68-2.95,4.42l-45.95,23.08c-1.35,.68-2.95,.68-4.3,0l-51.55-22.34L6.89,179.85c-2.34,1.18-5.19,.25-6.37-2.09,0,0,0,0,0,0C.18,177.09,0,176.36,0,175.62V78.41c0-2.22,1.46-4.17,3.59-4.8l25.36-9.78c.94,3.19,2.06,6.33,3.34,9.4l-22.29,8.59v86.19l37-18.63v-51.2c3.11,3.71,6.45,7.22,10,10.51ZM81.22,20.85c11.12,0,20.13,9.01,20.13,20.13s-9.01,20.13-20.13,20.13-20.13-9.01-20.13-20.13,9.01-20.13,20.13-20.13h0Z" style="fill: #000; fill-rule: evenodd;"/>
                </g>
            </svg>
            <h2>Manage Products</h2>
        </div>

        <!-- Toolbar -->
        <div class="pmt-toolbar">
            <input
                v-model.trim="searchQuery"
                type="search"
                class="pmt-search"
                placeholder="Search products..."
                aria-label="Search products"
            />
            <select v-model="filterProductFamily" class="pmt-filter">
                <option value="">All Product Families</option>
                <option v-for="family in uniqueProductFamilies" :key="family" :value="family">{{ family }}</option>
            </select>
            <select v-model="filterServiceNetwork" class="pmt-filter">
                <option value="">All Service Networks</option>
                <option v-for="network in uniqueServiceNetworks" :key="network" :value="network">{{ network }}</option>
            </select>
            <label class="pmt-checkbox-label">
                <input type="checkbox" v-model="filterAdditionalOnly" />
                Additional Services Only
            </label>
            <label class="pmt-checkbox-label">
                <input type="checkbox" v-model="filterStandardOnly" />
                Standard Only
            </label>
            <label class="pmt-checkbox-label">
                <input type="checkbox" v-model="filterDefaultOnly" />
                Default Products Only
            </label>
            <label class="pmt-checkbox-label">
                <input type="checkbox" v-model="showDiscontinued" />
                Show Discontinued
            </label>
            <button class="pmt-btn pmt-btn-refresh" @click="listProductData">Refresh</button>
            <button class="pmt-btn pmt-btn-export" :disabled="filteredProducts.length === 0" @click="exportCSV">Export CSV</button>
        </div>

        <loading-charts v-if="loadingProducts" class="loading-charts" />

        <!-- Table -->
        <div class="pmt-table-wrapper">
            <table class="pmt-table">
                <thead>
                    <tr>
                        <th @click="setSort('productName')" class="pmt-th-sortable">Product Name {{ sortIndicator('productName') }}</th>
                        <th @click="setSort('productCode')" class="pmt-th-sortable">Product Code {{ sortIndicator('productCode') }}</th>
                        <th @click="setSort('serviceCode')" class="pmt-th-sortable">Service Code {{ sortIndicator('serviceCode') }}</th>
                        <th @click="setSort('serviceGroup')" class="pmt-th-sortable">Service Group {{ sortIndicator('serviceGroup') }}</th>
                        <th @click="setSort('price')" class="pmt-th-sortable">Price {{ sortIndicator('price') }}</th>
                        <th @click="setSort('serviceNetwork')" class="pmt-th-sortable">Network {{ sortIndicator('serviceNetwork') }}</th>
                        <th @click="setSort('productSequence')" class="pmt-th-sortable">Seq {{ sortIndicator('productSequence') }}</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(group, groupIndex) in groupedProducts" :key="group.code">
                        <tr class="pmt-group-header" :class="groupIndex % 2 === 0 ? 'pmt-group-even' : 'pmt-group-odd'">
                            <td colspan="8" class="pmt-group-cell">
                                <strong>{{ group.name || group.code }}</strong>
                                <span class="pmt-group-code">{{ group.code }}</span>
                                <span class="pmt-group-count">{{ group.products.length }} product{{ group.products.length === 1 ? '' : 's' }}</span>
                            </td>
                        </tr>
                        <template v-for="product in group.products" :key="productKey(product)">
                            <tr :class="[groupIndex % 2 === 0 ? 'pmt-group-even-row' : 'pmt-group-odd-row', { 'pmt-discontinued-row': product.discontinued === true }]">
                                <td>
                                    {{ product.productName || '—' }}
                                    <span v-if="product.discontinued === true" class="pmt-discontinued-badge">Discontinued</span>
                                </td>
                                <td class="pmt-td-code">{{ product.productCode || '—' }}</td>
                                <td>{{ product.serviceCode || '—' }}</td>
                                <td>{{ product.serviceGroup || '—' }}</td>
                                <td>{{ product.price != null ? product.price : '—' }}</td>
                                <td>{{ product.serviceNetwork || '—' }}</td>
                                <td>{{ product.productSequence || '—' }}</td>
                                <td class="pmt-td-actions">
                                    <button class="pmt-btn pmt-btn-edit" @click="openEdit(product)">
                                        {{ isEditing(product) ? '▾ Editing' : '▸ Edit' }}
                                    </button>
                                    <button class="pmt-btn pmt-btn-copy" @click="copyProduct(product)" title="Use as template in Create Product">
                                        Copy
                                    </button>
                                </td>
                            </tr>
                            <!-- Inline edit row -->
                            <tr v-if="isEditing(product)" class="pmt-edit-row">
                                <td colspan="8">
                                    <div class="pmt-edit-container">
                                        <h4 class="pmt-edit-title">Editing {{ product.productCode }} / {{ product.serviceCode }}</h4>

                                        <p class="pmt-edit-hint">Only Price, Descriptions, Delivery Window, Tier Pricing, and the Discontinued flag can be edited. Other attributes are read-only.</p>

                                        <div class="pmt-edit-grid">
                                            <label class="pmt-edit-field">
                                                <span>Product Name</span>
                                                <input type="text" disabled v-model="editForm.productName" />
                                            </label>
                                            <label v-if="!editForm.additionalService" class="pmt-edit-field">
                                                <span>Product Family</span>
                                                <input type="text" disabled v-model="editForm.productFamily" />
                                            </label>
                                            <label class="pmt-edit-field">
                                                <span>Service Network</span>
                                                <input type="text" disabled v-model="editForm.serviceNetwork" />
                                            </label>
                                            <label class="pmt-edit-field">
                                                <span>Vendor</span>
                                                <input type="text" disabled v-model="editForm.vendor" />
                                            </label>
                                            <label v-if="editForm.additionalService" class="pmt-edit-field">
                                                <span>Service Group</span>
                                                <input type="text" disabled v-model="editForm.serviceGroup" />
                                            </label>
                                            <label v-if="!editUseTierPricing" class="pmt-edit-field">
                                                <span>Price</span>
                                                <input type="number" step="any" v-model.number="editForm.price" />
                                            </label>
                                            <label v-if="!editForm.additionalService" class="pmt-edit-field">
                                                <span>Product Sequence</span>
                                                <input type="text" disabled v-model="editForm.productSequence" />
                                            </label>
                                            <label v-if="editForm.serviceGroup" class="pmt-edit-field">
                                                <span>Service Group Sequence</span>
                                                <input type="text" disabled :value="editForm.serviceGroupSequence || nextServiceGroupSequence" />
                                            </label>
                                            <label class="pmt-edit-field">
                                                <span>Fuel Surcharge Code</span>
                                                <input type="text" disabled v-model="editForm.fuelSurchargeCode" />
                                            </label>
                                            <template v-if="editForm.additionalService">
                                                <label class="pmt-edit-field">
                                                    <span>Delivery Window Start</span>
                                                    <input type="time" v-model="editForm.deliveryWindowStart" />
                                                </label>
                                                <label class="pmt-edit-field">
                                                    <span>Delivery Window End</span>
                                                    <input type="time" v-model="editForm.deliveryWindowEnd" />
                                                </label>
                                                <label class="pmt-edit-field">
                                                    <span>Delivery Window Label</span>
                                                    <input type="text" v-model="editForm.deliveryWindowLabel" />
                                                </label>
                                            </template>
                                        </div>

                                        <div class="pmt-edit-checks">
                                            <label><input type="checkbox" disabled v-model="editForm.additionalService" /> Additional Service</label>
                                            <label><input type="checkbox" disabled v-model="editForm.defaultProduct" /> Default Product</label>
                                            <label><input type="checkbox" disabled v-model="editUseTierPricing" /> Use Tier Pricing</label>
                                            <label><input type="checkbox" v-model="editForm.discontinued" /> Discontinued</label>
                                        </div>

                                        <label class="pmt-edit-field pmt-edit-full">
                                            <span>Description</span>
                                            <textarea rows="2" v-model="editForm.description"></textarea>
                                        </label>
                                        <label class="pmt-edit-field pmt-edit-full">
                                            <span>Description (UI)</span>
                                            <textarea rows="2" v-model="editForm.descriptionUI"></textarea>
                                        </label>

                                        <!-- Tier Pricing (editable) -->
                                        <div v-if="editUseTierPricing" class="pmt-tier-container">
                                            <h4 class="pmt-tier-title">Tier Pricing</h4>
                                            <p class="pmt-edit-hint">With tier pricing on, the base price is set to 0 and pricing is driven by the tiers below. Tier ranges cannot overlap.</p>
                                            <table class="pmt-tier-table">
                                                <thead>
                                                    <tr>
                                                        <th>Start</th><th>End</th><th>Price</th><th>Slab</th><th>Allowed</th><th>Remove</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="(tier, tierIdx) in editForm.tierPrice" :key="tierIdx">
                                                        <td><input type="number" class="pmt-tier-input" v-model.number="tier.start" /></td>
                                                        <td><input type="number" class="pmt-tier-input" v-model.number="tier.end" /></td>
                                                        <td><input type="number" step="any" class="pmt-tier-input" v-model.number="tier.price" /></td>
                                                        <td class="pmt-tier-center"><input type="checkbox" v-model="tier.slab" /></td>
                                                        <td class="pmt-tier-center"><input type="checkbox" v-model="tier.allowed" /></td>
                                                        <td class="pmt-tier-center"><button class="pmt-btn pmt-btn-remove" @click="removeTier(tierIdx)">×</button></td>
                                                    </tr>
                                                    <tr v-if="!editForm.tierPrice || editForm.tierPrice.length === 0">
                                                        <td colspan="6" class="pmt-empty">No tier prices</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            <button class="pmt-btn pmt-btn-add-tier" @click="addTier">+ Add Tier</button>
                                            <p v-if="editTierPriceError" class="pmt-tier-error">{{ editTierPriceError }}</p>
                                        </div>

                                        <!-- Included Products (read-only) -->
                                        <div class="pmt-tier-container">
                                            <h4 class="pmt-tier-title">Included Products</h4>
                                            <table class="pmt-tier-table">
                                                <thead>
                                                    <tr>
                                                        <th>Product Code</th><th>Service Code</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="(included, incIdx) in editForm.includedProducts" :key="incIdx">
                                                        <td><input type="text" disabled class="pmt-tier-input pmt-included-input" v-model="included.productCode" /></td>
                                                        <td><input type="text" disabled class="pmt-tier-input pmt-included-input" v-model="included.serviceCode" /></td>
                                                    </tr>
                                                    <tr v-if="!editForm.includedProducts || editForm.includedProducts.length === 0">
                                                        <td colspan="2" class="pmt-empty">No included products</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div class="pmt-edit-buttons">
                                            <button class="pmt-btn pmt-btn-cancel" :disabled="savingProduct" @click="cancelEdit">Cancel</button>
                                            <button class="pmt-btn pmt-btn-save" :disabled="savingProduct" @click="saveProduct">
                                                {{ savingProduct ? 'Saving...' : 'Save Changes' }}
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </template>
                    <tr v-if="!groupedProducts.length">
                        <td colspan="8" class="pmt-empty">No products match your filters.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="pmt-pagination">
            <span class="pmt-pagination-info">{{ paginationStart }}–{{ paginationEnd }} of {{ filteredProducts.length }}</span>
            <button class="pmt-btn pmt-btn-page" :disabled="currentPage === 1" @click="currentPage--">‹ Prev</button>
            <button class="pmt-btn pmt-btn-page" :disabled="currentPage >= totalPages" @click="currentPage++">Next ›</button>
            <select v-model.number="pageSize" class="pmt-page-size">
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="999999">All</option>
            </select>
        </div>

        <!-- Datalists reused across edit rows -->
        <datalist id="pmt-family-list">
            <option v-for="family in uniqueProductFamilies" :key="family" :value="family" />
        </datalist>
        <datalist id="pmt-network-list">
            <option v-for="network in uniqueServiceNetworks" :key="network" :value="network" />
        </datalist>
        <datalist id="pmt-vendor-list">
            <option v-for="vendor in uniqueVendors" :key="vendor" :value="vendor" />
        </datalist>
        <datalist id="pmt-group-list">
            <option v-for="group in uniqueServiceGroups" :key="group" :value="group" />
        </datalist>
        <datalist id="pmt-productcode-list">
            <option v-for="code in uniqueProductCodes" :key="code" :value="code" />
        </datalist>
        <datalist id="pmt-servicecode-list">
            <option v-for="code in uniqueServiceCodes" :key="code" :value="code" />
        </datalist>
        <datalist id="pmt-fuel-list">
            <option v-for="code in uniqueFuelSurchargeCodes" :key="code" :value="code" />
        </datalist>
    </div>
    <alert-user v-if="toggleAlertBox" @closeAlertBox="closeAlertBox($event)" :message="alertMessage" />
</template>

<script>
import { API } from "aws-amplify";
import jsonexport from "jsonexport";
import AlertUser from "../../../components/Popups/AlertUser.vue";
import LoadingCharts from "../../ShipmentDashboard/LoadingCharts.vue";

export default {
    name: "ProductTable",
    components: { AlertUser, LoadingCharts },
    props: ["userData", "jwtToken"],
    emits: ["copyProduct"],

    data() {
        return {
            productData: [],
            loadingProducts: false,
            savingProduct: false,
            toggleAlertBox: false,
            alertMessage: "",

            searchQuery: "",
            filterProductFamily: "",
            filterServiceNetwork: "",
            filterAdditionalOnly: false,
            filterStandardOnly: false,
            filterDefaultOnly: false,
            showDiscontinued: false,

            sortField: "productCode",
            sortDirection: "asc",

            currentPage: 1,
            pageSize: 50,

            editingKey: null,
            editForm: {},
            editUseTierPricing: false
        };
    },

    watch: {
        userData() { if (this.userData) this.listProductData(); },
        searchQuery() { this.currentPage = 1; },
        filterProductFamily() { this.currentPage = 1; },
        filterServiceNetwork() { this.currentPage = 1; },
        filterAdditionalOnly() { this.currentPage = 1; },
        filterStandardOnly() { this.currentPage = 1; },
        filterDefaultOnly() { this.currentPage = 1; },
        showDiscontinued() { this.currentPage = 1; }
    },

    mounted() {
        if (this.userData) this.listProductData();
    },

    computed: {
        // Live inline warning for invalid tier ranges (overlap / start > end) in the edit form.
        editTierPriceError() {
            if (!this.editUseTierPricing) return null;
            const tiers = this.editForm.tierPrice || [];
            if (tiers.length === 0) return null;
            return this.validateTiers(tiers);
        },
        uniqueProductFamilies() {
            const set = new Set();
            this.productData.forEach(p => { if (p.productFamily) set.add(p.productFamily); });
            return Array.from(set).sort();
        },
        uniqueServiceGroups() {
            const set = new Set();
            this.productData.forEach(p => { if (p.serviceGroup) set.add(p.serviceGroup); });
            return Array.from(set).sort();
        },
        uniqueServiceNetworks() {
            const set = new Set();
            this.productData.forEach(p => { if (p.serviceNetwork) set.add(p.serviceNetwork); });
            return Array.from(set).sort();
        },
        uniqueVendors() {
            const set = new Set();
            this.productData.forEach(p => { if (p.vendor) set.add(p.vendor); });
            return Array.from(set).sort();
        },
        uniqueFuelSurchargeCodes() {
            const set = new Set();
            this.productData.forEach(p => { if (p.fuelSurchargeCode) set.add(p.fuelSurchargeCode); });
            return Array.from(set).sort();
        },
        uniqueProductCodes() {
            const set = new Set();
            this.productData.forEach(p => { if (p.productCode) set.add(p.productCode); });
            return Array.from(set).sort();
        },
        uniqueServiceCodes() {
            const set = new Set();
            this.productData.forEach(p => { if (p.serviceCode) set.add(p.serviceCode); });
            return Array.from(set).sort();
        },
        nextServiceGroupSequence() {
            // Highest existing serviceGroupSequence + 1, preserving leading-zero width.
            let highest = null;
            this.productData.forEach(p => {
                if (p.serviceGroupSequence != null && p.serviceGroupSequence !== "") {
                    if (highest === null || Number(highest) < Number(p.serviceGroupSequence)) {
                        highest = p.serviceGroupSequence;
                    }
                }
            });
            if (highest === null) return "01";
            const num = parseInt(highest, 10) + 1;
            return String(num).padStart(String(highest).length, "0");
        },

        filteredProducts() {
            let list = this.productData;

            // Hide discontinued products unless the user toggles "Show Discontinued".
            if (!this.showDiscontinued) list = list.filter(p => p.discontinued !== true);

            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase();
                list = list.filter(p =>
                    (p.productName || "").toLowerCase().includes(q) ||
                    (p.productCode || "").toLowerCase().includes(q) ||
                    (p.serviceCode || "").toLowerCase().includes(q) ||
                    (p.description || "").toLowerCase().includes(q) ||
                    (p.serviceGroup || "").toLowerCase().includes(q) ||
                    (p.serviceNetwork || "").toLowerCase().includes(q)
                );
            }

            if (this.filterProductFamily) list = list.filter(p => p.productFamily === this.filterProductFamily);
            if (this.filterServiceNetwork) list = list.filter(p => p.serviceNetwork === this.filterServiceNetwork);
            if (this.filterAdditionalOnly) list = list.filter(p => p.additionalService === true);
            if (this.filterStandardOnly) list = list.filter(p => p.additionalService === false);
            if (this.filterDefaultOnly) list = list.filter(p => p.defaultProduct === true);

            const field = this.sortField;
            const dir = this.sortDirection === "asc" ? 1 : -1;

            list = [...list].sort((a, b) => {
                const codeCompare = (a.productCode || "").localeCompare(b.productCode || "");
                if (codeCompare !== 0) return codeCompare;
                let valA = a[field];
                let valB = b[field];
                if (valA == null) valA = "";
                if (valB == null) valB = "";
                if (typeof valA === "number" && typeof valB === "number") return (valA - valB) * dir;
                return String(valA).localeCompare(String(valB)) * dir;
            });

            return list;
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.filteredProducts.length / this.pageSize));
        },
        paginatedProducts() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredProducts.slice(start, start + this.pageSize);
        },
        paginationStart() {
            return this.filteredProducts.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
        },
        paginationEnd() {
            return Math.min(this.currentPage * this.pageSize, this.filteredProducts.length);
        },

        groupedProducts() {
            const groups = [];
            let currentCode = null;
            let currentGroup = null;
            for (const product of this.paginatedProducts) {
                if (product.productCode !== currentCode) {
                    currentCode = product.productCode;
                    currentGroup = {
                        code: product.productCode || "Unknown",
                        name: this.getGroupName(product.productCode),
                        products: []
                    };
                    groups.push(currentGroup);
                }
                currentGroup.products.push(product);
            }
            return groups;
        }
    },

    methods: {
        productKey(product) {
            return `${product.productCode}-${product.serviceCode}`;
        },

        getGroupName(productCode) {
            const candidates = this.productData.filter(p => p.productCode === productCode && p.additionalService !== true);
            if (!candidates.length) {
                const any = this.productData.find(p => p.productCode === productCode);
                return any ? (any.productName || productCode) : productCode;
            }
            candidates.sort((a, b) => (parseInt(a.productSequence) || 999999) - (parseInt(b.productSequence) || 999999));
            return candidates[0].productName || productCode;
        },

        setSort(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
            } else {
                this.sortField = field;
                this.sortDirection = "asc";
            }
        },
        sortIndicator(field) {
            if (this.sortField !== field) return "";
            return this.sortDirection === "asc" ? "▲" : "▼";
        },

        isEditing(product) {
            return this.editingKey === this.productKey(product);
        },

        capitalizeEditField(field) {
            const value = this.editForm[field] || "";
            this.editForm[field] = value.toUpperCase();
        },

        copyProduct(product) {
            // Send a deep copy so the create form can edit it without touching the table.
            this.$emit("copyProduct", JSON.parse(JSON.stringify(product)));
        },

        exportCSV() {
            // Export the products currently matching the filters/search.
            const rows = this.filteredProducts.map(p => ({
                productCode: p.productCode || "",
                serviceCode: p.serviceCode || "",
                productName: p.productName || "",
                description: p.description || "",
                descriptionUI: p.descriptionUI || "",
                additionalService: p.additionalService === true,
                defaultProduct: p.defaultProduct === true,
                discontinued: p.discontinued === true,
                serviceNetwork: p.serviceNetwork || "",
                vendor: p.vendor || "",
                productFamily: p.productFamily || "",
                serviceGroup: p.serviceGroup || "",
                productSequence: p.productSequence || "",
                serviceGroupSequence: p.serviceGroupSequence || "",
                price: p.price != null ? p.price : "",
                fuelSurchargeCode: p.fuelSurchargeCode || "",
                deliveryWindowStart: p.deliveryWindowStart || "",
                deliveryWindowEnd: p.deliveryWindowEnd || "",
                deliveryWindowLabel: p.deliveryWindowLabel || "",
                tierPrice: Array.isArray(p.tierPrice) && p.tierPrice.length
                    ? p.tierPrice.map(t => `${t.start}-${t.end}:${t.price}${t.slab ? " (slab)" : ""}`).join(" | ")
                    : "",
                includedProducts: Array.isArray(p.includedProducts) && p.includedProducts.length
                    ? p.includedProducts.map(i => `${i.productCode}/${i.serviceCode}`).join(" | ")
                    : ""
            }));

            if (rows.length === 0) {
                this.toggleAlertBox = true;
                this.alertMessage = "No products to export.";
                return;
            }

            jsonexport(rows, (error, csv) => {
                if (error) {
                    this.toggleAlertBox = true;
                    this.alertMessage = "Error exporting products to CSV.";
                    return;
                }
                const blob = new Blob([csv], { type: "text/csv" });
                const anchor = document.createElement("a");
                anchor.href = window.URL.createObjectURL(blob);
                anchor.download = "Products.csv";
                anchor.click();
                window.URL.revokeObjectURL(anchor.href);
                anchor.remove();
            });
        },

        openEdit(product) {
            if (this.isEditing(product)) {
                this.cancelEdit();
                return;
            }
            this.editingKey = this.productKey(product);
            // Deep copy so edits don't mutate the table until saved.
            this.editForm = JSON.parse(JSON.stringify(product));
            if (!Array.isArray(this.editForm.tierPrice)) this.editForm.tierPrice = [];
            if (!Array.isArray(this.editForm.includedProducts)) this.editForm.includedProducts = [];
            // Tier pricing is considered "on" if the product already has tiers.
            this.editUseTierPricing = this.editForm.tierPrice.length > 0;
        },

        onToggleEditAdditionalService() {
            // Mirror the create form: keep only the fields relevant to the product type.
            if (this.editForm.additionalService) {
                // Additional services don't use product family / product sequence.
                this.editForm.productFamily = null;
                this.editForm.productSequence = null;
            } else {
                // Non-additional services don't use service group or delivery windows.
                this.editForm.serviceGroup = null;
                this.editForm.deliveryWindowStart = null;
                this.editForm.deliveryWindowEnd = null;
                this.editForm.deliveryWindowLabel = null;
            }
        },

        onToggleEditTierPricing() {
            if (this.editUseTierPricing) {
                this.editForm.price = 0;
                if (!Array.isArray(this.editForm.tierPrice) || this.editForm.tierPrice.length === 0) {
                    this.editForm.tierPrice = [];
                    this.addTier();
                }
            } else {
                this.editForm.tierPrice = [];
            }
        },

        cancelEdit() {
            this.editingKey = null;
            this.editForm = {};
            this.editUseTierPricing = false;
        },

        addTier() {
            if (!Array.isArray(this.editForm.tierPrice)) this.editForm.tierPrice = [];
            this.editForm.tierPrice.push({ start: 0, end: 0, price: 0, slab: false, allowed: true });
        },
        removeTier(idx) {
            this.editForm.tierPrice.splice(idx, 1);
        },
        // Validate tier ranges: numeric, start <= end, no overlap. Prices may be negative.
        // Returns an error string or null.
        validateTiers(tiers) {
            const list = Array.isArray(tiers) ? tiers : [];
            if (list.length === 0) return "Add at least one tier price or turn off tier pricing.";

            for (let i = 0; i < list.length; i++) {
                const start = Number(list[i].start);
                const end = Number(list[i].end);
                if (Number.isNaN(start) || Number.isNaN(end)) {
                    return `Tier ${i + 1} must have a numeric start and end.`;
                }
                if (start > end) {
                    return `Tier ${i + 1} start (${start}) cannot be greater than its end (${end}).`;
                }
            }

            const sorted = [...list].sort((a, b) => Number(a.start) - Number(b.start));
            for (let i = 1; i < sorted.length; i++) {
                if (Number(sorted[i].start) <= Number(sorted[i - 1].end)) {
                    return `Tier ranges overlap: ${sorted[i - 1].start}-${sorted[i - 1].end} and ${sorted[i].start}-${sorted[i].end}.`;
                }
            }
            return null;
        },
        addIncludedProduct() {
            if (!Array.isArray(this.editForm.includedProducts)) this.editForm.includedProducts = [];
            this.editForm.includedProducts.push({ productCode: "", serviceCode: "" });
        },
        removeIncludedProduct(idx) {
            this.editForm.includedProducts.splice(idx, 1);
        },

        async saveProduct() {
            if (this.savingProduct) return;

            // Block the save if tier pricing is on and the tiers are invalid.
            if (this.editUseTierPricing) {
                const tierError = this.validateTiers(this.editForm.tierPrice);
                if (tierError) {
                    this.toggleAlertBox = true;
                    this.alertMessage = "Invalid tier pricing — " + tierError;
                    return;
                }
            }

            this.savingProduct = true;

            // Build the update input. productCode + serviceCode are the key and must be included.
            const f = this.editForm;
            const input = {
                productCode: f.productCode,
                serviceCode: f.serviceCode,
                description: f.description,
                additionalService: !!f.additionalService,
                serviceNetwork: f.serviceNetwork,
                vendor: f.vendor,
                price: this.editUseTierPricing ? 0 : (f.price != null ? Number(f.price) : null),
                productName: f.productName,
                descriptionUI: f.descriptionUI,
                deliveryWindowEnd: f.additionalService ? (f.deliveryWindowEnd || null) : null,
                deliveryWindowStart: f.additionalService ? (f.deliveryWindowStart || null) : null,
                deliveryWindowLabel: f.additionalService ? (f.deliveryWindowLabel || null) : null,
                // Product family + product sequence only apply to non-additional-service products.
                productFamily: f.additionalService ? null : (f.productFamily || null),
                productSequence: f.additionalService ? null : (f.productSequence || null),
                // Service group only applies to additional-service products.
                serviceGroup: f.additionalService ? (f.serviceGroup || null) : null,
                // A service group sequence is only kept when a service group is set.
                // If a group is set but no sequence exists yet, assign the next available one.
                serviceGroupSequence: (f.additionalService && f.serviceGroup)
                    ? (f.serviceGroupSequence || this.nextServiceGroupSequence)
                    : null,
                fuelSurchargeCode: f.fuelSurchargeCode || null,
                useTaxCode: !!f.useTaxCode,
                getCost: !!f.getCost,
                defaultProduct: !!f.defaultProduct,
                discontinued: !!f.discontinued,
                tierPrice: this.editUseTierPricing
                    ? (f.tierPrice || []).map(t => ({
                        start: t.start != null ? Number(t.start) : null,
                        end: t.end != null ? Number(t.end) : null,
                        price: t.price != null ? Number(t.price) : null,
                        slab: !!t.slab,
                        allowed: t.allowed !== false
                    }))
                    : [],
                includedProducts: (f.includedProducts || [])
                    .filter(item => item.productCode && item.serviceCode)
                    .map(item => ({ productCode: item.productCode, serviceCode: item.serviceCode }))
            };

            // Inline mutation that avoids selecting non-nullable createdAt/updatedAt,
            // which older Product records don't have.
            const updateProductMutation = `mutation UpdateProduct($input: UpdateProductInput!){
                updateProduct(input: $input){
                    productCode
                    serviceCode
                }
            }`;

            try {
                await API.graphql({
                    query: updateProductMutation,
                    variables: { input },
                    authMode: "AMAZON_COGNITO_USER_POOLS"
                });
                this.toggleAlertBox = true;
                this.alertMessage = "Product updated.";
                this.cancelEdit();
                await this.listProductData();
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = e?.errors?.[0]?.message || "Unable to update product.";
            } finally {
                this.savingProduct = false;
            }
        },

        closeAlertBox(toggleAlertBox) {
            this.toggleAlertBox = toggleAlertBox;
        },

        async listProductData() {
            this.loadingProducts = true;
            const query = `query listProducts($nextToken: String) {
                listProducts(limit: 500, nextToken: $nextToken) {
                    items {
                        productCode
                        serviceCode
                        description
                        additionalService
                        serviceNetwork
                        vendor
                        price
                        productName
                        descriptionUI
                        deliveryWindowEnd
                        deliveryWindowStart
                        deliveryWindowLabel
                        productFamily
                        serviceGroup
                        serviceGroupSequence
                        productSequence
                        fuelSurchargeCode
                        useTaxCode
                        getCost
                        defaultProduct
                        discontinued
                        tierPrice {
                            start
                            end
                            price
                            slab
                            allowed
                        }
                        includedProducts {
                            productCode
                            serviceCode
                        }
                    }
                    nextToken
                }
            }`;

            try {
                let all = [];
                let nextToken = null;
                do {
                    const response = await API.graphql({
                        query,
                        variables: { nextToken },
                        authMode: "AMAZON_COGNITO_USER_POOLS"
                    });
                    const data = response.data.listProducts;
                    all = [...all, ...(data.items || [])];
                    nextToken = data.nextToken;
                } while (nextToken);
                this.productData = all;
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error getting products";
            } finally {
                this.loadingProducts = false;
            }
        }
    }
};
</script>

<style scoped>
.pmt-container {
    width: 100%;
    margin-top: 0;
}

.confirm-svg {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: #e4e4e4;
    border-radius: 999px;
    padding: 5px;
    width: fit-content;
    margin-bottom: 10px;
}

.confirm-svg svg {
    width: 35px;
    height: 35px;
    padding-right: 5px;
}

.confirm-svg h2 {
    margin: 0;
}

.pmt-toolbar {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.pmt-search,
.pmt-filter {
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: 10px;
    outline: none;
}

.pmt-search {
    flex: 1 1 220px;
}

.pmt-checkbox-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
}

.pmt-table-wrapper {
    width: 100%;
    overflow-x: auto;
    border-radius: 10px;
    border: 1px solid #eee;
}

.pmt-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.pmt-table th,
.pmt-table td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
}

.pmt-table thead th {
    background-color: #32ccfe;
    color: #fff;
    position: sticky;
    top: 0;
    white-space: nowrap;
}

.pmt-th-sortable {
    cursor: pointer;
    user-select: none;
}

.pmt-group-header td {
    background-color: #eef7ff;
    border-bottom: 1px solid #d5ecfb;
}

.pmt-group-code {
    margin-left: 10px;
    font-size: 11px;
    color: #6a6a6a;
}

.pmt-group-count {
    margin-left: 10px;
    font-size: 11px;
    color: #6a6a6a;
}

.pmt-group-even-row td {
    background-color: #fff;
}

.pmt-group-odd-row td {
    background-color: #fafafa;
}

.pmt-td-code {
    font-weight: bold;
}

/* Discontinued products: muted red row with a strikethrough on the name. */
.pmt-discontinued-row td {
    background-color: #fdecea !important;
    color: #b71c1c;
}

.pmt-discontinued-row td:first-child {
    text-decoration: line-through;
}

.pmt-discontinued-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-decoration: none;
    color: #fff;
    background-color: #d32f2f;
    border-radius: 10px;
    vertical-align: middle;
}

.pmt-empty {
    text-align: center;
    color: #6a6a6a;
    padding: 16px;
}

.pmt-btn {
    border: none;
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
    transition-duration: .25s;
    color: #fff;
}

.pmt-btn:hover {
    filter: brightness(0.95);
}

.pmt-btn:disabled {
    background-color: #b7b7b7;
    cursor: not-allowed;
}

.pmt-btn-edit {
    background-color: #32ccfe;
}

.pmt-btn-copy {
    background-color: #33f18a;
}

.pmt-td-actions {
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
}

.pmt-btn-refresh {
    background-color: #e4e4e4;
    color: #000;
}

.pmt-btn-export {
    background-color: #33f18a;
}

.pmt-btn-save {
    background-color: #33f18a;
}

.pmt-btn-cancel {
    background-color: #e4e4e4;
    color: #000;
}

.pmt-btn-remove {
    background-color: #fe804d;
    padding: 2px 10px;
}

.pmt-btn-add-tier {
    background-color: #32ccfe;
    margin-top: 8px;
}

.pmt-btn-page {
    background-color: #e4e4e4;
    color: #000;
}

.pmt-edit-row td {
    background-color: #f7fbff;
}

.pmt-edit-container {
    padding: 12px 6px;
}

.pmt-edit-title {
    margin: 0 0 12px 0;
    font-size: 15px;
}

.pmt-edit-hint {
    font-size: 12px;
    color: #6a6a6a;
    margin: 10px 0 0 0;
}

.pmt-tier-error {
    margin: 8px 0 0 0;
    font-size: 12px;
    color: #c0392b;
    font-weight: bold;
}

.pmt-edit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
}

.pmt-edit-field {
    display: flex;
    flex-direction: column;
    font-size: 12px;
    color: #3f3f3f;
}

.pmt-edit-field span {
    font-weight: bold;
    margin-bottom: 3px;
}

.pmt-edit-field input,
.pmt-edit-field textarea {
    padding: 8px;
    font-size: 13px;
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: 8px;
    outline: none;
}

.pmt-edit-full {
    margin-top: 10px;
}

.pmt-edit-checks {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 12px;
    font-size: 13px;
}

.pmt-edit-checks label {
    display: flex;
    align-items: center;
    gap: 6px;
}

.pmt-tier-container {
    margin-top: 16px;
    background-color: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 10px;
}

.pmt-tier-title {
    margin: 0 0 8px 0;
    font-size: 14px;
}

.pmt-tier-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.pmt-tier-table th,
.pmt-tier-table td {
    padding: 5px 8px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
}

.pmt-tier-center {
    text-align: center;
}

.pmt-tier-input {
    width: 80px;
    padding: 5px;
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: 6px;
    outline: none;
}

.pmt-included-input {
    width: 130px;
}

.pmt-edit-buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
}

.pmt-pagination {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    justify-content: flex-end;
}

.pmt-pagination-info {
    font-size: 13px;
    color: #6a6a6a;
}

.pmt-page-size {
    padding: 6px 10px;
    border: 1px solid rgba(0, 0, 0, .2);
    border-radius: 8px;
    outline: none;
}

/* Keep the whole table area within the viewport and scroll horizontally. */
.pmt-container {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.pmt-table-wrapper {
    -webkit-overflow-scrolling: touch;
}

/* The table needs a minimum width so columns stay readable; the wrapper scrolls. */
.pmt-table {
    min-width: 720px;
}

@media only screen and (max-width: 768px) {
    .pmt-toolbar {
        gap: 8px;
    }

    /* Search and filters stack full width so they don't overflow. */
    .pmt-search,
    .pmt-filter {
        flex: 1 1 100%;
        width: 100%;
        box-sizing: border-box;
    }

    .pmt-checkbox-label {
        flex: 1 1 100%;
    }

    /* Edit form fields become a single column. */
    .pmt-edit-grid {
        grid-template-columns: 1fr;
    }

    .pmt-edit-checks {
        gap: 12px;
    }

    .pmt-edit-buttons {
        flex-direction: column-reverse;
    }

    .pmt-edit-buttons .pmt-btn {
        width: 100%;
    }

    /* Pagination controls wrap and stay centered. */
    .pmt-pagination {
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
    }
}

@media only screen and (max-width: 480px) {
    .pmt-table {
        font-size: 12px;
        min-width: 620px;
    }

    .pmt-table th,
    .pmt-table td {
        padding: 6px 8px;
    }

    .pmt-btn {
        padding: 6px 10px;
        font-size: 12px;
    }

    .pmt-td-actions {
        flex-direction: column;
        gap: 4px;
        align-items: stretch;
    }
}
</style>
